import { useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { GetIcon } from '../../../icons';
import { usePhotoGridStore, usePhotoGridStoreMutation } from '../../usePhotoStore';
import { type AssetInfo, AssetType } from '../../utils';
import { ImageAsset } from './ImageAsset';

const renderYouTubeAsset = (asset: AssetInfo): ReactNode => (
    <iframe
        className="h-full w-full"
        data-testid="photo-grid-zoom-asset-youtube-video"
        src={`${asset.assetUrl}?&autoplay=1`}
        title={asset.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
    />
);

const renderBrandSpecialistVideoAsset = (asset: AssetInfo): ReactNode => (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
        controls
        autoPlay
        className="h-full object-contain"
        data-testid="photo-grid-zoom-asset-brand-specialist-video"
    >
        <source src={asset.assetUrl} type="video/mp4" title={asset.title} />
    </video>
);

function createAssetViewerFactory(asset?: AssetInfo): ReactNode {
    switch (asset?.assetType) {
        case AssetType.YOUTUBE:
            return renderYouTubeAsset(asset);
        case AssetType.BRAND_SPECIALIST_VIDEO:
            return renderBrandSpecialistVideoAsset(asset);
        case AssetType.IMAGE:
            return <ImageAsset asset={asset} />;
        default:
            return null;
    }
}

export const AssetSection: FC = () => {
    const { data } = usePhotoGridStore();
    const { nextAsset, previousAsset } = usePhotoGridStoreMutation();

    const assetViewer = useMemo(() => createAssetViewerFactory(data?.selectedAsset), [data]);

    return (
        <section
            data-testid="photo-grid-zoom-asset-section"
            className="flex flex-1 items-center justify-center overflow-hidden md:px-4"
        >
            <button
                data-testid="photo-grid-zoom-asset-prev-button"
                type="button"
                className="hidden pl-[1px] pr-4 md:flex"
                onClick={previousAsset}
            >
                <GetIcon name="navigation-arrow-left" size="lg" />
            </button>
            <div className="flex h-full w-full items-center justify-center">{assetViewer}</div>
            <button
                data-testid="photo-grid-zoom-asset-next-button"
                type="button"
                className="hidden pl-4 pr-[1px] md:flex"
                onClick={nextAsset}
            >
                <GetIcon name="navigation-arrow-right" size="lg" />
            </button>
        </section>
    );
};
